/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Slobodan Djordjevic
 */
import React, { useState, useEffect } from "react"
import { useLocation } from "react-router"
import { useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import { PersonalizeMoment } from "trux/dist/organisms/PersonalizeMoment"
import {
    Column,
    Modal,
    TitleSection,
    Label,
    ErrorRotate,
    useToken,
    getAuthToken,
    useIsLandscape,
} from "trux"
import {
    useEditCollectionItemMutation,
    useFullVideoMomentQuery,
    CollectionDocument,
    useCollectionQuery,
    useProfileQuery,
} from "../../generated/graphql"
import { traceIdPlusUrl } from "../../util/links"
import "./PersonalizeMomentPage.css"
import { useVideoSuspenders } from "trux/dist/organisms/VideoPlayer"

export const PersonalizeMomentPage = (props) => {
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)
    const [showConfirmation,] = useState(false)
    const isLandscapeMode = useIsLandscape()

    // Suspend the background video
    const { suspend, resume } = useVideoSuspenders(props.videoContainerRef)
    useEffect(() => {
        suspend()
        return () => {
            resume()
        }
    }, [props.videoContainerRef.current])

    const history = useHistory()
    const location = useLocation()

    const token = useToken("PersonalizeMomentDialog")
    const tokenReady = token !== null

    const profileHashKey = location?.state?.userHashKey
    const collectionTitle = location?.state?.playlistTitle
    const collectionHashKey = location?.state?.playlistHashKey
    const userPlaylistClipId = location?.state?.userPlaylistClipId

    const { data: collectionData } = useCollectionQuery({
        variables: {
            hash_key: collectionHashKey,
            token: token,
        },
        skip: !tokenReady,
    })

    const { data: profileData } = useProfileQuery({
        variables: {
            hash_key: profileHashKey,
            token: token,
        },
        skip: !tokenReady,
    })

    const { data: fullVideoData } = useFullVideoMomentQuery({
        variables: {
            collection_hash_key: collectionHashKey,
            user_playlist_clip_id: parseInt(userPlaylistClipId),
            token: token,
        },
    })

    const [editCollectionItem] = useEditCollectionItemMutation({
        variables: {
            token: token,
        },
        refetchQueries: [CollectionDocument, "collection"],
    })

    const handleOnClose = () => {
        const newPath = location.pathname.replace(/\/personalize.*$/gi, "")
        history.replace(newPath)
        document.dispatchEvent(new CustomEvent("trc-play"))
        if (typeof props.onClose === "function") props.onClose()
    }

    const checkValidToken = async (existingToken) => {
        let validToken = existingToken
        if (!validToken) {
            validToken = await getAuthToken()
        }
        return validToken
    }

    const handleOnSave = (updates) => {
        let newMoment = {}
        // Include moment startAt and duration
        if (updates.didAdjustStartTimeOrDuration) {
            const newDuration = updates.stopAt - updates.startAt
            const roundedNewDuration = Math.round(newDuration / 1000) * 1000
            newMoment["start_time"] = updates.startAt
            newMoment["duration"] = roundedNewDuration
        }
        // Include spotlight
        if (
            updates.didAdjustSpotlight ||
            updates.didAdjustStartTimeOrDuration
        ) {
            if (updates.spotlightEnabled) {
                newMoment["spotlights"] = [
                    {
                        center: [
                            updates.spotlightCenterX,
                            updates.spotlightCenterY,
                        ],
                        corr: 0,
                        height: 0,
                        trace_number: "-1",
                        user_id: profileData.user_id,
                        utc: updates.spotlightStartAtUTC,
                        width: 0,
                    },
                ]
            } else {
                newMoment["spotlights"] = []
            }
        }

        // Add the tags
        if (updates.tags && updates.tags.length > 0) {
            const reformattedTags = updates.tags
                .filter((tag) => tag.selected)
                .map((tag) => {
                    return {
                        name: tag.name,
                    }
                })
            newMoment["tags"] = reformattedTags
        }
        // Add the title
        if (updates.didAdjustTitle) {
            newMoment["title"] = updates.title
        }

        if (Object.keys(newMoment).length > 0) {
            checkValidToken(token)
                .then((validToken) => {
                    if (!validToken) {
                        handleOnClose()
                        dispatch({
                            type: "COMMON_SET",
                            key: "toast",
                            value: "Something went wrong",
                        })
                        return
                    }
                    setLoading(true)
                    editCollectionItem({
                        variables: {
                            token: validToken,
                            collection_hash_key: collectionHashKey,
                            user_playlist_clip_id: parseInt(userPlaylistClipId),
                            edit: newMoment,
                        },
                    }).then((response) => {
                        if (response?.data?.editCollectionItem?.success) {
                            setLoading(false)
                            // setShowConfirmation(true)
                            // Dispatch toast
                            let collectionLink = traceIdPlusUrl(
                                profileHashKey,
                                collectionHashKey,
                                parseInt(userPlaylistClipId)
                            )
                            dispatch({
                                type: "COMMON_SET",
                                key: "bottomToast",
                                value: {
                                    message: `<p>Your highlight was personalized in <a href="${collectionLink}">${collectionTitle}</a>.`,
                                    playlistLink: collectionLink,
                                    show: true,
                                    link: null,
                                    linkText: null,
                                    linkState: null
                                }
                            })
                            handleOnClose()
                        } else {
                            setLoading(false)
                            handleOnClose()
                            console.log(
                                "Something went wrong with moment editing"
                            )
                            dispatch({
                                type: "COMMON_SET",
                                key: "toast",
                                value: "Something went wrong",
                            })
                        }
                    })
                })
                .catch((err) => {
                    console.log(err)
                })
        }
    }

    const goToTraceId = (e) => {
        if (e) {
            e.preventDefault()
        }
        let link = traceIdPlusUrl(
            profileHashKey,
            collectionHashKey,
            parseInt(userPlaylistClipId)
        )
        Object.assign(document.createElement("a"), {
            target: "_blank",
            href: link,
        }).click()
    }

    const collection = collectionData ? collectionData.collection : null

    const theMomentItem = collection?.items?.find(
        (aMoment) =>
            aMoment.user_playlist_clip_id === parseInt(userPlaylistClipId)
    )

    const fullVideoMoment =
        fullVideoData && fullVideoData.fullVideoMoment
            ? fullVideoData.fullVideoMoment
            : null
    const theMoment = theMomentItem ? theMomentItem.moment : null

    if (!fullVideoMoment || !theMoment) {
        return null
    }

    return (
        <>
            {!isLandscapeMode && (
                <Column
                    trkContext="Personalize Moment Page"
                    className="PersonalizeMomentPage"
                >
                    <PersonalizeMoment
                        fullGameMoment={fullVideoMoment}
                        onClose={handleOnClose}
                        onSave={handleOnSave}
                        loading={loading}
                        moment={theMoment}
                        open={true}
                        showConfirmation={showConfirmation}
                        goToTraceId={goToTraceId}
                        tagsType={"trace"}
                    />
                </Column>
            )}
            {isLandscapeMode && (
                <Modal open={true}>
                    <TitleSection
                        className="header"
                        onCloseClick={handleOnClose}
                    >
                        <Label
                            type={Label.Type.Heading4}
                            content="Personalize Moment"
                        />
                    </TitleSection>
                    <ErrorRotate />
                </Modal>
            )}
        </>
    )
}
